import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Input,
  Heading,
  Text,
  VStack,
  Select,
  useToast,
  Switch,
  Divider,
  HStack,
  Spinner,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { getEshopSettings, updateEshopSettings, getClubInfo, EshopSettings, ClubInfo } from '../services/eshopApi';

const SetupPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [clubInfo, setClubInfo] = useState<ClubInfo | null>(null);
  const [formData, setFormData] = useState<EshopSettings>({
    default_currency: 'CZK',
    default_country: 'CZ',
    support_email: '',
    support_phone: '',
    terms_url: '',
    returns_policy_url: '',
  });

  // Packeta toggle state (will be stored in shipping_options_json)
  const [packetaEnabled, setPacketaEnabled] = useState(false);

  useEffect(() => {
    const initData = async () => {
      try {
        setLoading(true);
        // Load Club Info to pre-fill defaults
        const info = await getClubInfo();
        setClubInfo(info);

        // Load existing settings if any
        const settings = await getEshopSettings();
        
        // Merge defaults if settings are empty
        setFormData(prev => ({
          ...prev,
          ...settings,
          // If support email/phone are empty in settings, pre-fill from club info
          support_email: settings.support_email || info.contact_email || '',
          support_phone: settings.support_phone || info.contact_phone || '',
          default_country: settings.default_country || info.contact_country || 'CZ',
        }));

        // Parse packeta enabled state from shipping_options_json
        if (settings.shipping_options_json) {
          try {
            const opts = JSON.parse(settings.shipping_options_json);
            if (opts.packeta_enabled) {
              setPacketaEnabled(true);
            }
          } catch (e) {
            console.error("Failed to parse shipping options", e);
          }
        }
      } catch (error) {
        console.error("Setup init failed", error);
        toast({
          title: 'Chyba načítání dat',
          description: 'Nepodařilo se načíst informace o klubu nebo nastavení.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    initData();
  }, [toast]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Prepare shipping options
      const shippingOptions = {
        packeta_enabled: packetaEnabled,
      };

      const payload: EshopSettings = {
        ...formData,
        shipping_options_json: JSON.stringify(shippingOptions),
      };

      await updateEshopSettings(payload);

      toast({
        title: 'Nastavení uloženo',
        description: 'E-shop je připraven k použití.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Redirect to admin dashboard
      navigate('/admin');
    } catch (error) {
      console.error("Save failed", error);
      toast({
        title: 'Chyba ukládání',
        description: 'Nastavení se nepodařilo uložit.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box py={10} textAlign="center">
        <Spinner size="xl" />
        <Text mt={4}>Načítám konfiguraci...</Text>
      </Box>
    );
  }

  return (
    <Container maxW="2xl" py={8}>
      <VStack spacing={6} align="stretch" bg="white" p={8} borderRadius="lg" boxShadow="md">
        <Box>
          <Heading size="lg" mb={2}>E-shop Setup Wizard</Heading>
          <Text color="gray.600">
            Základní nastavení vašeho e-shopu. Některé údaje jsme předvyplnili z nastavení klubu 
            <strong> {clubInfo?.club_name}</strong>.
          </Text>
        </Box>

        <Divider />

        <Heading size="md">1. Lokalizace a měna</Heading>
        <HStack spacing={4}>
          <FormControl id="default_country">
            <FormLabel>Výchozí země</FormLabel>
            <Select name="default_country" value={formData.default_country} onChange={handleChange}>
              <option value="CZ">Česká republika</option>
              <option value="SK">Slovensko</option>
            </Select>
          </FormControl>
          <FormControl id="default_currency">
            <FormLabel>Měna</FormLabel>
            <Select name="default_currency" value={formData.default_currency} onChange={handleChange}>
              <option value="CZK">CZK (Kč)</option>
              <option value="EUR">EUR (€)</option>
            </Select>
          </FormControl>
        </HStack>

        <Heading size="md" mt={4}>2. Zákaznická podpora</Heading>
        <Text fontSize="sm" color="gray.500">Tyto údaje se zobrazí v patičce a emailech zákazníkům.</Text>
        <FormControl id="support_email" isRequired>
          <FormLabel>Email podpory</FormLabel>
          <Input 
            name="support_email" 
            type="email" 
            value={formData.support_email} 
            onChange={handleChange} 
            placeholder="eshop@mojklub.cz" 
          />
        </FormControl>
        <FormControl id="support_phone">
          <FormLabel>Telefon podpory</FormLabel>
          <Input 
            name="support_phone" 
            type="tel" 
            value={formData.support_phone} 
            onChange={handleChange} 
            placeholder="+420 123 456 789" 
          />
        </FormControl>

        <Heading size="md" mt={4}>3. Obchodní podmínky</Heading>
        <FormControl id="terms_url">
          <FormLabel>Odkaz na VOP</FormLabel>
          <Input 
            name="terms_url" 
            value={formData.terms_url} 
            onChange={handleChange} 
            placeholder="https://www.mojklub.cz/obchodni-podminky" 
          />
        </FormControl>
        <FormControl id="returns_policy_url">
          <FormLabel>Odkaz na Reklamační řád</FormLabel>
          <Input 
            name="returns_policy_url" 
            value={formData.returns_policy_url} 
            onChange={handleChange} 
            placeholder="https://www.mojklub.cz/reklamace" 
          />
        </FormControl>

        <Heading size="md" mt={4}>4. Doprava</Heading>
        <FormControl display="flex" alignItems="center">
          <FormLabel htmlFor="packeta-switch" mb="0">
            Povolit Zásilkovnu (Packeta)
          </FormLabel>
          <Switch 
            id="packeta-switch" 
            isChecked={packetaEnabled} 
            onChange={(e) => setPacketaEnabled(e.target.checked)} 
          />
        </FormControl>
        {packetaEnabled && (
          <Text fontSize="xs" color="blue.500">
            Packeta widget bude aktivní v košíku. Ujistěte se, že máte nastavené PACKETA_ API klíče v .env.
          </Text>
        )}

        <Divider my={4} />

        <Button 
          colorScheme="blue" 
          size="lg" 
          onClick={handleSave} 
          isLoading={saving}
          loadingText="Ukládám..."
        >
          Dokončit nastavení
        </Button>
      </VStack>
    </Container>
  );
};

export default SetupPage;
